<template>
  <div class="account account--login d-md-flex">
    <div class="account__image d-none d-md-block">
      <lazy-image
        :image="{
          url: 'https://images.prismic.io/asphalte/c3f09a85-0644-4224-9d54-3c840e321a3e_login.BMP?auto=compress,format&rect=466,0,1024,1228&w=768&h=921',
          dimensions: { width: 768, height: 920 },
        }"
      />
    </div>

    <div class="account__container d-flex justify-center pt-2 pb-2">
      <transition name="fade-from-bottom" mode="out-in">
        <div
          v-if="mode === modes.HOME"
          key="firstStep"
          class="account__subcontainer"
        >
          <h1 class="h2 account__title mb-2">{{ $t('account.title') }}</h1>

          <p class="text-color-secondary-2 text-small mb-2">
            {{ $t('account.p1') }}
          </p>

          <form id="account-check" novalidate @submit="checkUserStatus">
            <form-input
              id="email"
              v-model="email"
              type="email"
              :label="$t('account.labels.email')"
              label-visible
              :placeholder="$t('account.placeholder.email')"
              autocomplete="username"
              first-submit
              required
            />

            <transition name="fade-from-left">
              <div
                v-if="status === statusAvailable.GUEST_ACCOUNT"
                class="text-small"
              >
                {{ $t('account.activationLinkSent') }}
              </div>
            </transition>

            <div class="mt-2">
              <primary-button
                type="submit"
                :text="$t('account.nextButton')"
                align="center"
                :loading="loading"
                :disabled="loading === true"
                with-arrow
              />
            </div>

            <transition name="fade-from-bottom">
              <p
                v-if="wait"
                class="text-extra-small text-color-secondary text-center mt-1"
              >
                {{ $t('account.wait') }}
              </p>
            </transition>

            <transition name="fade-from-bottom">
              <p
                v-if="unexpectedError !== false"
                class="text-small text-color-error invalid__field mt-0-5 text-center"
              >
                {{ $t('register.error.unexpectedError') }}
              </p>
            </transition>
          </form>
        </div>

        <login
          v-else-if="mode === modes.LOGIN"
          key="login"
          :email="email"
          :is-remembered="isRemembered"
          @go-back-event="goBack"
          @redirect-event="redirectWhenLoggedIn"
        />

        <register
          v-else-if="mode === modes.REGISTER"
          key="register"
          :email="email"
          @go-back-event="goBack"
          @redirect-event="redirectWhenLoggedIn"
        />

        <register
          v-else-if="mode === modes.ACTIVATE"
          :id="id"
          key="activate"
          :email="email"
          :activation-token="activationToken"
          :activate-mode="true"
          @go-back-event="goBack"
          @redirect-event="redirectWhenLoggedIn"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import * as EmailValidator from 'email-validator'

import Login from '@/components/account/Login'
import Register from '@/components/account/Register'
import PrimaryButton from '@/components/buttons/Primary'
import FormInput from '@/components/form/Input'
import LazyImage from '@/components/Image/LazyImage'

import { COOKIE_EMAIL_KEY } from '@/const'
import services from '@/services'

export default {
  name: 'Account',
  components: { FormInput, Register, Login, PrimaryButton, LazyImage },
  data() {
    return {
      isRemembered: false,
      status: null,
      statusAvailable: {
        GUEST_ACCOUNT: 'DISABLED',
        NO_ACCOUNT: 'NOT_FOUND',
        HAS_ACCOUNT: 'ENABLED',
      },
      email: '',
      id: null,
      invalidEmail: false,
      loading: false,
      windowWidth: null,
      activationToken: null,
      modes: {
        REGISTER: 'register',
        LOGIN: 'login',
        ACTIVATE: 'activate',
        HOME: 'home',
      },
      unexpectedError: false,
      wait: false,
    }
  },

  computed: {
    mode() {
      if (this.activationToken !== null && this.email !== null) {
        return this.modes.ACTIVATE
      }
      if (this.status !== null) {
        if (this.status === this.statusAvailable.HAS_ACCOUNT) {
          return this.modes.LOGIN
        }
        if (this.status === this.statusAvailable.NO_ACCOUNT) {
          return this.modes.REGISTER
        }
      }
      return this.modes.HOME
    },
  },

  mounted() {
    this.windowWidth = window.innerWidth
    const url = new URL(window.location.href)

    const email = url.searchParams.get('email')
    const id = url.searchParams.get('id')
    const activationToken = url.searchParams.get('activationToken')
    const accountStatus = url.searchParams.get('accountStatus')
    if (id) {
      this.id = id
    }
    if (activationToken) {
      this.activationToken = activationToken
    }
    if (email) {
      this.email = decodeURI(email)
    } else {
      this.checkEmailCookie()
    }
    if (accountStatus) {
      this.status = accountStatus
    }

    this.$track.page(
      { title: this.$t('account.title') },
      this.$route,
      this.$nuxt.context.from
    )
  },

  methods: {
    checkEmailCookie() {
      if (this.$cookies.isKey(COOKIE_EMAIL_KEY)) {
        const email = this.$cookies.get(COOKIE_EMAIL_KEY)
        this.status = this.statusAvailable.HAS_ACCOUNT
        this.email = email
        this.isRemembered = true
      }
    },

    updateEmail(value) {
      this.email = value
    },

    goBack() {
      this.status = null
      this.isRemembered = false
    },

    checkUserStatus(event) {
      if (event) {
        event.preventDefault()
      }
      if (this.email !== null) {
        this.email = this.email.trim()
      }
      if (EmailValidator.validate(this.email)) {
        this.loading = true
        services.accountManagerService
          .getCustomerStatus(this.email)
          .then((res) => {
            this.status = res.data.data.status

            if (res.data.data.status === this.statusAvailable.GUEST_ACCOUNT) {
              // todo : virer le parsing et récupérer seulement l'id
              const idArray = res.data.data.id.split('/')
              const id = idArray[idArray.length - 1]
              this.wait = true

              const locale = this.$i18n.locales.find((locale) => {
                return locale.code === this.$i18n.locale
              })

              services.accountManagerService
                .sendActivationMail(
                  id,
                  this.email,
                  locale.code,
                  this.$route.params.context
                )
                .then((_) => {
                  setTimeout(() => {
                    this.loading = false
                    this.wait = false
                    // Deactivating submit button for 10 minutes
                  }, 10 * 60 * 1000)
                })
                .catch((err) => {
                  // sentry.sendErrorToSentry(
                  //   err,
                  //   'accountManager',
                  //   'sendActivationMail'
                  // );
                  this.$logError.captureException(err)
                  this.unexpectedError = true
                })
            } else {
              this.loading = false
            }
          })
          .catch((err) => {
            // sentry.sendErrorToSentry(err, 'accountManager', 'getCustomerStatus')
            this.$logError.captureException(err)
            this.loading = false
            this.unexpectedError = true
          })
      } else {
        this.invalidEmail = true
      }
    },
    async redirectWhenLoggedIn() {
      const url = new URL(window.location.href)
      const checkoutUrl = url.searchParams.get('checkout_url')

      if (checkoutUrl) {
        try {
          const multipassURL =
            await services.multipassLoginService.buildMultipassLoginUrl(
              this.email,
              checkoutUrl
            )
          window.location = multipassURL
        } catch (err) {
          window.location = checkoutUrl
        }
        return false
      }

      const returnUrl = url.searchParams.get('return_url')
      if (returnUrl) {
        return this.$router.push(this.localePath(returnUrl))
      }

      return this.$router.push(
        this.localePath(`/${this.$route.params.context}/account`)
      )
    },
  },
}
</script>
